/**
 * Zero-config default bookable windows for a calendar block: Monday to Friday,
 * 09:00 to 17:00, weekends off. One source of truth shared by the authoring UI
 * (the factory that stamps a new block and the weekly-hours editor) and the
 * publish gate, so the default never drifts between where it is written and
 * where it is validated. Dependency-free (no React), so a service can import it
 * as safely as a component.
 *
 * The live-session worker keeps two behavioral twins for schemas that predate
 * stamped windows: deriveCalendarBookingDescriptor's DEFAULT_BOOKABLE_WINDOWS
 * (auth-handshake.ts) and the slot computer's DEFAULT_WINDOWS. All three mean
 * the same Monday-Friday 09:00-17:00; a change here must change them too.
 */

import type { CalendarBookingFieldConfig } from '../../../types';

/** A single bookable weekly window. */
export type BookableWindow = NonNullable<CalendarBookingFieldConfig['bookableWindows']>[number];

/**
 * Monday to Friday, 09:00 to 17:00, with Saturday and Sunday disabled. Every
 * weekday is present (enabled or not) so toggling one on or off never has to
 * synthesize a missing row.
 */
export const DEFAULT_BOOKABLE_WINDOWS: BookableWindow[] = ([0, 1, 2, 3, 4, 5, 6] as const).map(
  (weekday) => ({
    weekday,
    enabled: weekday >= 1 && weekday <= 5,
    start: '09:00',
    end: '17:00',
  }),
);
